import { formatDate } from '@/lib/format'
import { titleCase } from '@/lib/utils'

import type {
  EvalRunLifecycleStatus,
  RelayEvalRun,
  RelayEvalRunAssertionResult,
} from './types'

export interface EvalRunAssertionStats {
  total: number
  passed: number
  failed: number
}

// `failed` folds in evaluator `error` verdicts; `inconclusive` is neither
// passed nor failed (derive it as total - passed - failed when needed).
export function getAssertionStats(
  results: RelayEvalRunAssertionResult[]
): EvalRunAssertionStats {
  let passed = 0
  let failed = 0
  for (const result of results) {
    if (result.verdict === 'passed') {
      passed += 1
    } else if (result.verdict === 'failed' || result.verdict === 'error') {
      failed += 1
    }
  }
  return { total: results.length, passed, failed }
}

export const EVAL_RUN_STATUS_CLASS_NAMES: Record<
  EvalRunLifecycleStatus,
  string
> = {
  queued: 'bg-gray-100 text-neutral-low-contrast',
  provisioning: 'bg-warning-ui-bg text-warning-solid',
  running: 'bg-primary-ui-bg text-primary-solid',
  completed: 'bg-success-ui-bg text-success-solid',
  failed: 'bg-error-ui-bg text-error-solid',
  cancelled: 'bg-gray-100 text-neutral-low-contrast',
}

const COMPACT_EVAL_RUN_STATUS_LABELS: Record<EvalRunLifecycleStatus, string> = {
  queued: 'Pending',
  provisioning: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

export function isActiveEvalRun(run: RelayEvalRun): boolean {
  return ['queued', 'provisioning', 'running'].includes(run.status)
}

export function formatEvalRunStatus(status: EvalRunLifecycleStatus): string {
  return titleCase(status)
}

export function getCompactEvalRunStatusLabel(
  status: EvalRunLifecycleStatus
): string {
  return COMPACT_EVAL_RUN_STATUS_LABELS[status]
}

export function formatEvalRunChannel(channel: RelayEvalRun['channel']): string {
  return titleCase(channel)
}

export function formatEvalRunDateTime(
  value: string | null | undefined,
  emptyLabel = '-'
): string {
  if (!value) return emptyLabel
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return emptyLabel
  try {
    return formatDate(value)
  } catch {
    return emptyLabel
  }
}

export function formatEvalRunCreatedAt(value: string | undefined): string {
  return formatEvalRunDateTime(value, 'Created recently')
}
